import React, { useState, useEffect, useMemo, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { Modal } from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import {
  SG_GET_DEBRIEF_PDF_REPORT_ORG_TEMPLATES,
  SG_CREATE_DEBRIEF_PDF_REPORT,
  SG_GET_DEBRIEF_PDF_REPORT,
  SG_GET_CULTURE_AUDIT_REPORTS,
  SG_DELETE_DEBRIEF_PDF_REPORT,
  SG_GET_DEBRIEF_SCHEDULES,
  SG_GET_ORGANIZATION_GROUP,
  CLEAR_DEBRIEF_PDF_REPORT,
} from "constants/actions";
import AnalyticsBreadcrumb from "../NewAnalytics/AnalyticsBreadcrumb";
import { reportIconNew } from "../../layouts/LeaderLayout/Icons";
import { SURVEY_TYPE } from "constants/surveys";

const AutomatedReports = () => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [view, setView] = useState("library"); // "library" or "reports"
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [loadingCard, setLoadingCard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, reportId: null, reportName: null });

  // Form state
  const [reportName, setReportName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDebriefSchedule, setSelectedDebriefSchedule] = useState(null);

  const {
    org_templates,
    org_templates_fetched,
    debrief_pdf_report,
    fetched,
    created,
  } = useSelector(
    (state) => ({
      org_templates: state.debrief_pdf_report?.org_templates || [],
      org_templates_fetched: state.debrief_pdf_report?.org_templates_fetched || false,
      debrief_pdf_report: Array.isArray(state.debrief_pdf_report?.debrief_pdf_report)
        ? state.debrief_pdf_report.debrief_pdf_report
        : state.debrief_pdf_report?.debrief_pdf_report?.results || [],
      fetched: state.debrief_pdf_report?.fetched || false,
      created: state.debrief_pdf_report?.created || false,
    }),
    shallowEqual
  );

  const {
    core_data,
    get_auth,
    get_organizations,
    get_debrief_schedules,
    employeeId,
    get_culture_audit_reports,
    outcomesQuestionsList,
    organizationId,
    organizationGroups,
  } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_debrief_schedules: state.debrief_schedule?.debrief_schedule?.results || [],
      employeeId: Number(state.auth?.employee_id),
      get_culture_audit_reports: state.debrief_schedule?.culture_audit_reports,
      outcomesQuestionsList: state.surveyquestions?.outcome_questions || [],
      organizationId: Number(state.auth?.organization_id),
      organizationGroups: Array.isArray(state.organization_group?.organization_groups)
        ? state.organization_group.organization_groups
        : [],
    }),
    shallowEqual
  );

  const organizationIdLegacy = get_auth?.organization_id;
  const resolvedOrganizationId = organizationId || Number(organizationIdLegacy);

  // Fetch templates on mount
  useEffect(() => {
    if (!org_templates_fetched) {
      dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT_ORG_TEMPLATES });
    }
  }, [dispatch, org_templates_fetched]);

  // Ensure debrief schedules are available for dropdown
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    dispatch({
      type: SG_GET_DEBRIEF_SCHEDULES,
      payload: `page=1&page_size=500&type_of=${SURVEY_TYPE.AUDIT}&organization=${resolvedOrganizationId}`,
    });
  }, [dispatch, resolvedOrganizationId]);

  // Fetch generated reports
  useEffect(() => {
    if (view === "reports") {
      dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT, payload: "report_type=3" });
    }
  }, [dispatch, view]);

  // Refresh reports after creation
  useEffect(() => {
    if (created) {
      addToast("Report queued. Refreshing History…", {
        appearance: "success",
        autoDismiss: true,
      });
      dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT, payload: "report_type=3" });
      dispatch({ type: CLEAR_DEBRIEF_PDF_REPORT });
    }
  }, [dispatch, created, addToast]);

  // Ensure engagement (culture audit) reports are available to derive survey series
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    const hasReports = Array.isArray(get_culture_audit_reports?.response) && get_culture_audit_reports.response.length > 0;
    if (hasReports) return;
    dispatch({
      type: SG_GET_CULTURE_AUDIT_REPORTS,
      payload: `audit-type-id=${SURVEY_TYPE.AUDIT}&organization=${resolvedOrganizationId}`,
    });
  }, [dispatch, resolvedOrganizationId, get_culture_audit_reports?.response]);

  // Ensure organization groups are available for naming
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    if (organizationGroups?.length) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: resolvedOrganizationId,
        group_type: "survey",
        page: 1,
        page_size: 500,
      },
    });
  }, [dispatch, resolvedOrganizationId, organizationGroups?.length]);

  // Get categories from core_data
  const categories = core_data?.categories?.[0]?.categories || [];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowGenerateModal(true);
    setReportName("");
    setSelectedCategory(null);
    setSelectedDebriefSchedule(null);
  };

  // Helpers to normalize ids and names (match NewAnalytics)
  const extractNumericId = (value) => {
    if (value == null) return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const id = extractNumericId(item);
        if (id != null) return id;
      }
      return null;
    }
    if (typeof value === "object") {
      if ("id" in value) return extractNumericId(value.id);
      if ("pk" in value) return extractNumericId(value.pk);
      if ("value" in value) return extractNumericId(value.value);
      return null;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    return null;
  };

  const pickFirstNumericId = (...candidates) => {
    for (const candidate of candidates) {
      const id = extractNumericId(candidate);
      if (id != null) return id;
    }
    return null;
  };

  const extractDisplayName = (value) => {
    if (value == null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const name = extractDisplayName(item);
        if (name) return name;
      }
      return null;
    }
    if (typeof value === "object") {
      const candidates = [
        value.name,
        value.title,
        value.group_name,
        value.groupName,
        value.team_name,
        value.display_name,
        value.displayName,
        value.label,
      ];
      for (const candidate of candidates) {
        const name = extractDisplayName(candidate);
        if (name) return name;
      }
    }
    return null;
  };

  const pickFirstDisplayName = (...candidates) => {
    for (const candidate of candidates) {
      const name = extractDisplayName(candidate);
      if (name) return name;
    }
    return null;
  };

  // Build maps for schedule/group metadata (align with NewAnalytics)
  const scheduleMapById = useMemo(() => {
    return (get_debrief_schedules || []).reduce((map, sched) => {
      const schedId = extractNumericId(sched?.id);
      if (schedId != null) {
        map.set(schedId, sched);
      }
      return map;
    }, new Map());
  }, [get_debrief_schedules]);

  const organizationGroupNameMap = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(organizationGroups) ? organizationGroups : [];
    list.forEach((group) => {
      const id = Number(group?.id);
      if (Number.isFinite(id) && id > 0) {
        const name = group?.name || group?.title || group?.group_name || group?.team_name;
        if (name) {
          map.set(id, name);
        }
      }
    });
    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];
    responses.forEach((response) => {
      const groupObj = response?.organization_group;
      const id = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group_id,
        response?.team_id
      );
      if (id == null) return;
      const name =
        response?.name ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name;
      if (name) {
        map.set(id, name);
      }
    });
    return map;
  }, [organizationGroups, get_culture_audit_reports?.response]);

  const scheduleToGroupIdMap = useMemo(() => {
    const map = new Map();
    (get_debrief_schedules || []).forEach((sched) => {
      const schedId = extractNumericId(sched?.id);
      const groupId = sched?.organization_group;
      if (schedId == null || groupId == null) return;
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const groupName = groupNameFromMap || sched?.group_name || null;
      map.set(schedId, { groupId, groupName });
    });
    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];
    responses.forEach((response) => {
      const schedId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );
      const groupObj = response?.organization_group;
      const groupId = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );
      if (schedId == null || groupId == null) return;
      const existing = map.get(schedId);
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const candidateName =
        groupNameFromMap ||
        existing?.groupName ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name ||
        null;
      map.set(schedId, { groupId, groupName: candidateName });
    });
    return map;
  }, [get_debrief_schedules, get_culture_audit_reports?.response, organizationGroupNameMap]);

  // Map resolved debrief_schedule id -> { questionSortOrder, outcomeSortOrder, surveyName } (align keys to schedule ids)
  const scheduleSortOrdersById = useMemo(() => {
    const map = new Map();
    const responses = Array.isArray(get_culture_audit_reports?.response) ? get_culture_audit_reports.response : [];
    responses.forEach((item) => {
      const responseId = extractNumericId(item?.id);
      const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;
      const fallbackScheduleId = pickFirstNumericId(
        item?.debrief_schedule,
        item?.debrief_schedule_id,
        item?.schedule,
        item?.schedule_id,
        item?.survey_schedule,
        item?.survey_schedule_id,
        item?.survey_event,
        item?.survey_event_id
      );
      const schedId = directSchedule ? responseId : fallbackScheduleId;
      if (schedId == null) return;
      const questionSortOrder = item?.question_sort_order || item?.question?.sort_order || null;
      const outcomeSortOrder = item?.additional_questions?.outcome_question?.question?.sort_order || null;
      const surveyName = item?.question?.name || item?.name || null;
      if (!map.has(schedId)) {
        map.set(schedId, { questionSortOrder, outcomeSortOrder, surveyName });
      }
    });
    return map;
  }, [get_culture_audit_reports?.response, scheduleMapById]);

  // Build dropdown options from debrief history (align with NewAnalytics history list)
  const debriefHistoryOptions = useMemo(() => {
    const responses = Array.isArray(get_culture_audit_reports?.response) ? [...get_culture_audit_reports.response] : [];
    if (responses.length === 0) return [];
    // Sort newest first
    responses.sort((a, b) => (extractNumericId(b?.id) || 0) - (extractNumericId(a?.id) || 0));
    const bySchedule = new Map();
    responses.forEach((response) => {
      const sortOrder = response?.question?.sort_order;
      const responseId = extractNumericId(response?.id);
      const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;
      const closeDate = directSchedule?.close_at || response?.date || null;
      const fallbackScheduleId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );
      const resolvedDebriefScheduleId = directSchedule ? responseId : fallbackScheduleId;
      if (resolvedDebriefScheduleId == null) return;
      const scheduleGroupMeta = scheduleToGroupIdMap.get(resolvedDebriefScheduleId) || null;
      const scheduleGroupId = scheduleGroupMeta?.groupId ?? null;
      const groupNameFromMap = scheduleGroupId != null ? organizationGroupNameMap.get(scheduleGroupId) : null;
      const organizationGroupName = pickFirstDisplayName(
        response?.name,
        groupNameFromMap,
        scheduleGroupMeta?.groupName,
        directSchedule?.organization_group,
        directSchedule?.group_name,
        response?.organization_group_name,
        response?.organization_group,
        response?.group,
        response?.team,
        response?.group_name,
        response?.team_name,
        sortOrder ? `Group ${sortOrder}` : "Group"
      );
      const questionTitle = response?.question?.name || (sortOrder ? `Survey ${sortOrder}` : "Survey");
      const primaryLabel = organizationGroupName || questionTitle;
      const formattedDate = closeDate ? new Date(closeDate).toLocaleDateString() : "N/A";
      const existing = bySchedule.get(resolvedDebriefScheduleId);
      const dt = closeDate ? new Date(closeDate) : new Date(0);
      // Keep the latest entry per schedule id
      if (!existing || (existing._dateObj || new Date(0)) < dt) {
        bySchedule.set(resolvedDebriefScheduleId, {
          value: resolvedDebriefScheduleId,
          label: primaryLabel,
          formattedDate,
          _dateObj: dt,
        });
      }
    });
    // Return sorted by date desc
    return Array.from(bySchedule.values()).sort((a, b) => (b._dateObj || 0) - (a._dateObj || 0));
  }, [get_culture_audit_reports?.response, scheduleToGroupIdMap, organizationGroupNameMap, scheduleMapById]);

  const handleGenerateReport = async () => {
    if (!selectedTemplate || !reportName || !selectedCategory || !selectedDebriefSchedule) {
      return;
    }

    // Infer sort orders from the selected debrief schedule
    const schedMeta = scheduleSortOrdersById.get(Number(selectedDebriefSchedule)) || {};
    const inferredQuestionSortOrder = schedMeta.questionSortOrder;
    const inferredOutcomeSortOrder = schedMeta.outcomeSortOrder;

    setGenerating(true);
    setLoadingCard(selectedTemplate.id);

    const payload = {
      employee: employeeId,
      organization: resolvedOrganizationId,
      debrief_schedule: selectedDebriefSchedule,
      report_type: 3,
      status: 5,
      pdf_template_report: selectedTemplate.id,
      report_name: reportName,
      // Move ancillary params under additional_info
      additional_info: {
        parent_anchor_id: selectedCategory,
        ...(inferredQuestionSortOrder != null && {
          survey_question_sort_order: Number(inferredQuestionSortOrder),
        }),
        ...(inferredOutcomeSortOrder != null && {
          outcome_question_sort_order: Number(inferredOutcomeSortOrder),
        }),
      },
    };

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      dispatch({ type: SG_CREATE_DEBRIEF_PDF_REPORT, payload });
      setShowGenerateModal(false);
      setView("reports");
      addToast("Report generation started. Redirecting to History…", {
        appearance: "success",
        autoDismiss: true,
      });
      setSelectedTemplate(null);
      setReportName("");
      setSelectedCategory(null);
      setSelectedDebriefSchedule(null);
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setGenerating(false);
      setLoadingCard(null);
    }
  };

  // Build Engagement survey series (by sort_order) from culture audit reports
  const engagementSeriesOptions = useMemo(() => {
    const responses = Array.isArray(get_culture_audit_reports?.response) ? get_culture_audit_reports.response : [];
    if (responses.length === 0) return [];
    const grouped = responses.reduce((acc, response) => {
      const sortOrder = response?.question?.sort_order;
      if (!sortOrder) return acc;
      if (!acc[sortOrder]) {
        acc[sortOrder] = {
          value: sortOrder,
          label: response?.question?.name || `Survey ${sortOrder}`,
        };
      }
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => a.value - b.value);
  }, [get_culture_audit_reports?.response]);

  // We no longer render outcome selection; it's inferred from selected schedule

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleDeleteClick = (reportId, reportName) => {
    setDeleteConfirmModal({ isOpen: true, reportId, reportName });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmModal.reportId) {
      const reportName = deleteConfirmModal.reportName || 'Report';
      const reportId = deleteConfirmModal.reportId;
      
      // Close modal first
      setDeleteConfirmModal({ isOpen: false, reportId: null, reportName: null });
      
      // Dispatch delete action (saga will handle API call)
      dispatch({ type: SG_DELETE_DEBRIEF_PDF_REPORT, payload: reportId });
      
      // Show toast immediately
      addToast(`${reportName} deleted successfully`, {
        appearance: "success",
        autoDismiss: true,
      });
      
      // Refresh the table after a short delay to ensure API call completes
      // The reducer will remove the item immediately when DELETE_DEBRIEF_PDF_REPORT is dispatched
      setTimeout(() => {
        dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT, payload: "report_type=3" });
      }, 800);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ isOpen: false, reportId: null, reportName: null });
  };
 

  const renderIcon = (color) => {
    const c = color || "#64748b";
    return reportIconNew(c);
  };

  const palette = [
    { fg: "#2D70E2", bg: "#D5E2F9" },
    { fg: "#27CDA7", bg: "#D4F5ED" },
    { fg: "#F15062", bg: "#FCDCE0" },
    { fg: "#FEA711", bg: "#FFEDCF" },
  ];

  return (
    <Container>
      <AnalyticsBreadcrumb />
      
      <Header>
        <HeaderTitle>
          {view === "library" ? "Report Templates" : "Generated Reports"}
        </HeaderTitle>
        <SegmentedControl>
          <SegmentedTab
            active={view === "library"}
            onClick={() => setView("library")}
            isFirst
          >
            <LibraryIcon active={view === "library"} />
            <span>Library</span>
          </SegmentedTab>
          <SegmentedTab
            active={view === "reports"}
            onClick={() => setView("reports")}
            isLast
          >
            <HistoryIcon active={view === "reports"} />
            <span>History</span>
          </SegmentedTab>
        </SegmentedControl>
      </Header>

      {view === "library" && (
        <Section>
          <CardsGrid>
            {org_templates.map((template, idx) => {
              const scheme = {
                fg: palette[idx % palette.length].fg,
                bg: palette[idx % palette.length].bg,
              };
              return (
                <AnalyticsCard
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  isLoading={loadingCard === template.id}
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  <CardTop>
                    <IconBox bg={scheme.bg}>
                      <IconWrapper>{renderIcon(scheme.fg)}</IconWrapper>
                    </IconBox>
                    <CardTitle>{template.org_template_name || template.template_name || `Template #${template.id}`}</CardTitle>
                  </CardTop>

                  <CardBody>
                    <CardDescription>
                      {template.global_template
                        ? "Global template available to all organizations"
                        : template.org_template
                        ? "Organization-specific template"
                        : "Template"}
                    </CardDescription>
                    {template.params?.layout && (
                      <TemplateInfo>
                        <InfoItem>Layout: {template.params.layout}</InfoItem>
                        {template.params.sections && (
                          <InfoItem>
                            Sections: {template.params.sections.join(", ")}
                          </InfoItem>
                        )}
                      </TemplateInfo>
                    )}
                  </CardBody>

                  <CardFooter>
                    {loadingCard === template.id ? (
                      <LoadingSpinner />
                    ) : (
                      <OpenButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTemplateSelect(template);
                        }}
                        aria-label={`Generate ${template.org_template_name || template.template_name || `Template #${template.id}`}`}
                      >
                        <span>Generate Report</span>
                        <ExploreArrow />
                      </OpenButton>
                    )}
                  </CardFooter>
                </AnalyticsCard>
              );
            })}
          </CardsGrid>
        </Section>
      )}

      {view === "reports" && (
        <Section>
          {debrief_pdf_report.length === 0 ? (
            <EmptyState>
              <EmptyText>No reports generated yet.</EmptyText>
              <EmptyButton onClick={() => setView("library")}>
                Browse Templates
              </EmptyButton>
            </EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableHeaderRow>
                    <TableHeaderCell>Report Name</TableHeaderCell>
                    <TableHeaderCell>Created At</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "right" }}>Actions</TableHeaderCell>
                  </TableHeaderRow>
                </TableHead>
                <TableBody>
                  {debrief_pdf_report.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <ReportNameText>{report.org_template_name || report.report_name || `Report #${report.id}`}</ReportNameText>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(report.created_at)}</DateText>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={report.status}>
                          {report.status_text || "Unknown"}
                        </StatusBadge>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <ActionsCell>
                          <ActionButton onClick={() => {}} disabled>
                            Download
                          </ActionButton>
                          <DeleteButton onClick={() => handleDeleteClick(report.id, report.org_template_name || report.report_name)}>
                            <DeleteIcon />
                          </DeleteButton>
                        </ActionsCell>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>
      )}

      <Modal
        onClose={() => setShowGenerateModal(false)}
        open={showGenerateModal}
        closeIcon={true}
      >
        <Modal.Header>
          <ModalTitle>Generate Report</ModalTitle>
        </Modal.Header>
        <Modal.Content>
          {selectedTemplate && (
            <FormContainer>
              <FormField>
                <Label>Template</Label>
                <TemplateDisplay>{selectedTemplate.org_template_name || selectedTemplate.template_name || `Template #${selectedTemplate.id}`}</TemplateDisplay>
              </FormField>

              <FormField>
                <Label>Report Name *</Label>
                <Input
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="Enter report name"
                />
              </FormField>

              <FormField>
                <Label>Category *</Label>
                <Select
                  value={selectedCategory || ""}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                <Label>Debrief Schedule *</Label>
                <Select
                  value={selectedDebriefSchedule || ""}
                  onChange={(e) => setSelectedDebriefSchedule(Number(e.target.value))}
                >
                  <option value="">Select a debrief schedule</option>
                  {debriefHistoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {`${opt.label} - ${opt.formattedDate}`}
                    </option>
                  ))}
                </Select>
              </FormField>
            </FormContainer>
          )}
        </Modal.Content>
        <Modal.Actions>
          <CancelButton onClick={() => setShowGenerateModal(false)}>
            Cancel
          </CancelButton>
          <GenerateButton
            onClick={handleGenerateReport}
            disabled={
              !reportName ||
              !selectedCategory ||
              !selectedDebriefSchedule ||
              generating
            }
          >
            {generating ? "Generating..." : "Generate Report"}
          </GenerateButton>
        </Modal.Actions>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteConfirmModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        message={`Are you sure you want to delete "${deleteConfirmModal.reportName || 'this report'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Container>
  );
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const cascadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(2px); }
  60% { opacity: 1; transform: translateY(0) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
const Container = styled.div`
  position: relative;
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 140%;
    transform: translateX(-50%);
    z-index: -1;
    pointer-events: none;
    background:
      radial-gradient(1200px 500px at 10% -10%, rgba(45, 112, 226, 0.12), transparent 60%),
      radial-gradient(1100px 460px at 110% 10%, rgba(39, 205, 167, 0.16), transparent 55%),
      radial-gradient(900px 380px at 50% -10%, rgba(255, 255, 255, 0.55), transparent 70%);
    opacity: 0.35;
    filter: saturate(100%);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 27px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
  flex: 1;
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  padding: 4px;
  gap: 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  }
`;

const SegmentedTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${(props) => (props.active ? "#111827" : "transparent")};
  color: ${(props) => (props.active ? "#ffffff" : "#64748b")};
  border: none;
  border-radius: ${(props) => {
    if (props.isFirst) return "8px 0 0 8px";
    if (props.isLast) return "0 8px 8px 0";
    return "0";
  }};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
  z-index: ${(props) => (props.active ? "1" : "0")};

  &:hover {
    color: ${(props) => (props.active ? "#ffffff" : "#111827")};
  }

  &:active {
    transform: scale(0.97);
  }

  span {
    transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
  }

  svg {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
  }

`;

const LibraryIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="4" height="12" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
    <rect x="10" y="4" width="4" height="14" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
    <rect x="16" y="8" width="4" height="10" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
  </svg>
);

const HistoryIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke={active ? "#ffffff" : "#64748b"} strokeWidth="1.5" fill="none" opacity={active ? "1" : "0.7"} />
    <path d="M12 8v4l3 3" stroke={active ? "#ffffff" : "#64748b"} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity={active ? "1" : "0.7"} />
  </svg>
);

const Section = styled.section`
  margin-bottom: 48px;
  animation: ${slideIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AnalyticsCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.2s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;
  padding: 16px;
  animation: ${cascadeIn} 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform, opacity;
  opacity: 0;
  min-height: 190px;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(148, 163, 184, 0.32);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  }

  ${props => props.isLoading && `
    pointer-events: none;
    opacity: 0.8;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: ${({ bg }) => bg || '#f8fafc'};
  border: 1px solid #eef2f7;
  transition: all 0.25s ease;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  
  svg {
    width: 22px;
    height: 22px;
    display: block;
  }
`;

const CardBody = styled.div`
  margin-top: 10px;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  text-align: left;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 8px 0;
  line-height: 1.5;
  font-weight: 400;
  text-align: left;
`;

const TemplateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

const InfoItem = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #0f172a;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
    border-radius: 6px;
  }
`;

const ExploreArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 16L16 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M9 8h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #94a3b8;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-right: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 20px 0;
`;

const EmptyButton = styled.button`
  padding: 10px 24px;
  background: #2D70E2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;


// Table Components
const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8fafc;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px 18px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 14px 18px;
  font-size: 14px;
  color: #1e293b;
`;

const ReportNameText = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const DateText = styled.div`
  color: #64748b;
  font-size: 14px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    const status = props.status;
    if (status === 1) return "#e0f2fe"; // New
    if (status === 5) return "#fef3c7"; // Queued for Processing
    return "#f1f5f9"; // Default
  }};
  color: ${(props) => {
    const status = props.status;
    if (status === 1) return "#0369a1"; // New
    if (status === 5) return "#92400e"; // Queued for Processing
    return "#64748b"; // Default
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #1e293b;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  padding: 6px;
  background: transparent;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #fef2f2;
    border-color: rgba(239, 68, 68, 0.4);
    color: #dc2626;
  }
`;

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

// Confirmation Dialog Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ConfirmationContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ConfirmationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

const ConfirmationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #1e293b;
  }
`;

const ConfirmationBody = styled.div`
  padding: 24px;
`;

const ConfirmationMessage = styled.p`
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
`;

const ConfirmButton = styled.button`
  padding: 10px 20px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ConfirmationContent onClick={e => e.stopPropagation()}>
        <ConfirmationHeader>
          <ConfirmationTitle>{title}</ConfirmationTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ConfirmationHeader>
        
        <ConfirmationBody>
          <ConfirmationMessage>{message}</ConfirmationMessage>
        </ConfirmationBody>
        
        <ConfirmationActions>
          <CancelButton onClick={onClose}>
            {cancelText}
          </CancelButton>
          <ConfirmButton onClick={onConfirm}>
            {confirmText}
          </ConfirmButton>
        </ConfirmationActions>
      </ConfirmationContent>
    </ModalOverlay>
  );
};

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const TemplateDisplay = styled.div`
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    color: #1e293b;
  }
`;

const GenerateButton = styled.button`
  padding: 10px 20px;
  background: #2D70E2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default AutomatedReports;

